/**
 * UpdateBanner — bottom-left toast that appears when a new game version
 * has been deployed while the player has the game open.
 * Dismissed by hitting REFRESH (reloads page) or "later" (hides for this session).
 * Disappears automatically after a real refresh since the new bundle
 * will match the live version.json.
 */
import { useEffect } from 'react';
import { useVersionCheck, isElectronApp } from '../hooks/useVersionCheck';

export default function UpdateBanner() {
  const { updateAvailable, liveVersion, dismiss } = useVersionCheck();

  // BETA 20.17 — Force a HARD update: clear any Cache Storage + service workers,
  // then reload. A plain reload() can be served the stale cached index.html on
  // some browsers / handheld PWAs (Dylan kept getting stuck on old builds). This
  // busts that so REFRESH actually pulls the new bundle.
  const forceUpdate = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
    } catch { /* ignore — best-effort */ }
    window.location.reload();
  };

  // BETA 20.64 — controller path. This banner is a DOM toast OUTSIDE the
  // gamepad focus system, so controller players could never press REFRESH —
  // one reason Dylan kept playing stale builds on the ROG Ally. While the
  // banner is visible, gamepadInput's MENU ticker routes SELECT (button 8)
  // here. Menu-only on purpose: in-game SELECT is the pause toggle, and a
  // forced reload mid-run would eat the player's run.
  useEffect(() => {
    if (!updateAvailable) return;
    (window as unknown as { __updateBannerRefresh?: () => void }).__updateBannerRefresh = forceUpdate;
    return () => {
      delete (window as unknown as { __updateBannerRefresh?: () => void }).__updateBannerRefresh;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <>
      {/* Slide-in keyframe injected once */}
      <style>{`
        @keyframes gzSlideIn {
          from { transform: translateX(-110%); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }
      `}</style>

      <div style={{
        // BETA 23.10 — COMPACT toast (Dylan: "covering way too much screen").
        // Small single-row strip pinned to the bottom edge, out of gameplay.
        position:    'fixed',
        bottom:       8,
        left:         8,
        zIndex:       99999,
        background:  'rgba(4, 8, 4, 0.92)',
        border:      '1px solid rgba(57,255,20,0.7)',
        borderRadius: 4,
        padding:     '6px 10px',
        fontFamily:  '"Share Tech Mono", monospace',
        boxShadow:   '0 2px 10px rgba(0,0,0,0.5)',
        maxWidth:     420,
        display:     'flex',
        alignItems:  'center',
        gap:          10,
        animation:   'gzSlideIn 0.35s ease-out',
        userSelect:  'none',
        fontSize:    '0.66rem',
      }}>

        <span style={{
          display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
          background: '#39ff14', boxShadow: '0 0 5px #39ff14', flexShrink: 0,
        }} />
        <span style={{ color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
          <b style={{ color: '#39ff14' }}>{liveVersion}</b>
          {isElectronApp() ? ' available — relaunch the updated app' : ' available'}
        </span>
        {!isElectronApp() && (
          <button
            onClick={forceUpdate}
            style={{
              background: '#39ff14', color: '#000', border: 'none', borderRadius: 3,
              padding: '3px 10px', fontFamily: '"Share Tech Mono", monospace',
              fontSize: '0.66rem', fontWeight: 'bold', letterSpacing: '0.08em',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            REFRESH
          </button>
        )}
        <button
          onClick={dismiss}
          title="Hide for this version"
          style={{
            background: 'transparent', color: 'rgba(255,255,255,0.45)',
            border: '1px solid rgba(255,255,255,0.18)', borderRadius: 3,
            padding: '3px 8px', fontFamily: '"Share Tech Mono", monospace',
            fontSize: '0.62rem', cursor: 'pointer', flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>
    </>
  );
}
